import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { focus as camFocus, lights, pose } from "./Player";
import { GalleryLight } from "./GalleryLight";
import { flameFrag, glowFrag, hallVert, plainVert, roomFrag, shaftFrag, wispFrag } from "./hallShaders";
import { archTap, ReturnPortal, Wall } from "./Walls";
import { FeelingWall } from "./FeelingWall";
import { glideTo } from "./Player";
import { createLiving } from "../../lib/living";
import { noteTap } from "../../lib/perf";
import { SECTION_IDS, SECTIONS } from "../../lib/sections";
import { ARCH, CORR, corrX, FIRE, LOVE_C, NATURE, regionAt, ROOM_H, SPIRIT } from "../../lib/world";

export const LIGHT = {
  love: { light: "#FFE4D2", shade: "#B0628A", dim: 0.04, vivid: 1.3, lift: 1.2, gamma: 0.82, paint: "#FFEDE2" },
  nature: { light: "#FFFBEA", shade: "#C9D9AE", dim: 0.06, haze: 0.025, hazeCol: "#F8F3DC", petal: 1, vivid: 1.68, lift: 1.2, gamma: 0.76, paint: "#FFFFFF" },
  spirit: { light: "#FFC680", shade: "#4A3A92", dim: 0.22, vivid: 1.3, lift: 1.25, gamma: 0.76, paint: "#FFE6C8" },
  thoughts: { light: "#DCE6FA", shade: "#2A3E78", dim: 0.24, vivid: 1.18, lift: 1.25, gamma: 0.7, paint: "#FFFFFF" },
  fire: { light: "#FFE0A0", shade: "#EFA978", dim: 0.34, vivid: 1.2, lift: 1.0, gamma: 1, paint: "#FFF2DC" },
};
let halo = null;
// warm wall-wash of light around each diya
const haloTex = () => {
  if (halo) return halo;
  const c = document.createElement("canvas");
  c.width = c.height = 128;
  const g = c.getContext("2d");
  const gr = g.createRadialGradient(64, 92, 4, 64, 80, 64);
  gr.addColorStop(0, "rgba(255,190,110,0.85)");
  gr.addColorStop(0.45, "rgba(255,150,70,0.32)");
  gr.addColorStop(1, "rgba(255,120,40,0)");
  g.fillStyle = gr;
  g.fillRect(0, 0, 128, 128);
  halo = new THREE.CanvasTexture(c);
  halo.colorSpace = THREE.SRGBColorSpace;
  return halo;
};
const aspect = (t) => (t?.image?.width ? t.image.width / t.image.height : 1);
const additive = (frag, uniforms) => new THREE.ShaderMaterial({ vertexShader: plainVert, fragmentShader: frag, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, uniforms });
window.__afaeLive = window.__afaeLive || {};

function roomMaterial(zone, a, b, surf, u, { uvLen = 0, side = THREE.FrontSide } = {}) {
  const L = LIGHT[zone];
  return new THREE.ShaderMaterial({
    vertexShader: hallVert,
    fragmentShader: roomFrag,
    side,
    uniforms: {
      uTime: u.uTime, uLite: u.uLite, uA: { value: a }, uB: { value: b }, uAspA: { value: aspect(a) }, uAspB: { value: aspect(b) },
      uSurf: { value: surf }, uH: { value: ROOM_H[zone] }, uUvLen: { value: uvLen }, uDim: { value: L.dim }, uPetal: { value: L.petal || 0 }, uHaze: { value: L.haze || 0 }, uPale: { value: 0 }, uVivid: { value: L.vivid }, uLift: { value: L.lift }, uGamma: { value: L.gamma },
      uTint: { value: new THREE.Color(SECTIONS[zone].tint) }, uLight: { value: new THREE.Color(L.light) }, uShade: { value: new THREE.Color(L.shade) }, uHazeCol: { value: new THREE.Color(L.hazeCol || "#ffffff") },
    },
  });
}

function ribbon(side, y0, y1) {
  const N = 64;
  const pos = [];
  const uv = [];
  const idx = [];
  let s = 0, px = 0, pz = 0;
  for (let i = 0; i <= N; i++) {
    const z = CORR.z0 + ((CORR.z1 - CORR.z0) * i) / N;
    const xa = corrX(z) + (side === 0 ? -CORR.hw : side * CORR.hw);
    const xb = side === 0 ? corrX(z) + CORR.hw : xa;
    if (i) s += Math.hypot(xa - px, z - pz);
    px = xa;
    pz = z;
    pos.push(xa, y0, z, xb, y1, z);
    uv.push(s, 0, s, 1);
  }
  for (let i = 0; i < N; i++) idx.push(i * 2, i * 2 + 1, i * 2 + 2, i * 2 + 1, i * 2 + 3, i * 2 + 2);
  for (let i = 0; i < uv.length; i += 2) uv[i] /= s;
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  g.computeVertexNormals();
  return { g, len: s };
}
const RIB = { l: ribbon(-1, 0, ROOM_H.thoughts), r: ribbon(1, 0, ROOM_H.thoughts), floor: ribbon(0, 0, 0), ceil: ribbon(0, ROOM_H.thoughts, ROOM_H.thoughts) };
const LOVE_GAP = Math.asin(ARCH.w / 2 / LOVE_C.r);

let liveCount = 0;
export function HungPainting({ painting, slot, texture, zone, region, meshesRef, onHover, onOpen, onEnter, hero = false, maxW = 1.7, maxH = 1.5 }) {
  const ar = aspect(texture) || painting.aspect_ratio || 1;
  const w = Math.min(maxW, maxH * ar);
  const h = w / ar;
  const home = hero ? "hall" : zone;
  const key = hero ? `hero-${painting.id}` : painting.id;
  const mat = useMemo(() => new THREE.MeshBasicMaterial({ map: texture, color: new THREE.Color(LIGHT[zone].paint), toneMapped: false }), [texture, zone]);
  const glow = useMemo(() => additive(glowFrag, { uColor: { value: new THREE.Color(SECTIONS[zone].glow) }, uNear: { value: 0 }, uTime: { value: 0 }, uPad: { value: 0.45 }, uBox: { value: new THREE.Vector2(w + 0.12, h + 0.12) } }), [zone, w, h]);
  const frame = useMemo(() => new THREE.MeshBasicMaterial({ color: zone === "spirit" && !hero ? "#F4E3BC" : "#FBF5EC", toneMapped: false }), [zone, hero]);
  // an elegant frame: a slim gilded moulding with a darker inner lip around the cream mat
  const moulding = useMemo(() => new THREE.MeshBasicMaterial({ color: "#B89254", toneMapped: false }), []);
  const lip = useMemo(() => new THREE.MeshBasicMaterial({ color: "#6E5632", toneMapped: false }), []);
  const live = useRef({ on: false, eng: null, tex: null, lit: zone === "thoughts" && !hero ? 0.2 : 1 });
  const level = useRef(0);
  const tint = useMemo(() => new THREE.Color(LIGHT[zone].paint), [zone]);
  const pure = useMemo(() => new THREE.Color("#FFFFFF"), []);
  const stop = () => {
    const s = live.current;
    if (!s.on) return;
    s.eng.stop();
    s.tex.dispose();
    Object.assign(s, { on: false, eng: null, tex: null });
    liveCount--;
    mat.map = texture;
    mat.needsUpdate = true;
  };
  useEffect(() => stop, []); // eslint-disable-line react-hooks/exhaustive-deps
  useFrame((_, dt) => {
    const s = live.current;
    const dx = slot.x - pose.x;
    const dz = slot.z - pose.z;
    const d = Math.hypot(dx, dz) || 0.001;
    const facing = -(dx * Math.sin(pose.yaw) + dz * Math.cos(pose.yaw)) / d;
    const U = glow.uniforms;
    const near = (1 - THREE.MathUtils.smoothstep(d, 1.6, 6)) * (facing > 0.2 ? 1 : 0.35);
    U.uTime.value = performance.now() / 1000;
    U.uNear.value += (near - U.uNear.value) * Math.min(1, dt * 3);
    const want = region === home && d < 8.5 && facing > 0.35 && (painting.effects || []).length > 0;
    if (want && !s.on && liveCount < 3) {
      const c = document.createElement("canvas");
      s.eng = createLiving(c, painting, { width: 512, height: Math.round(512 / ar), small: true });
      s.tex = new THREE.CanvasTexture(c);
      s.tex.colorSpace = THREE.SRGBColorSpace;
      s.on = true;
      liveCount++;
    } else if (!want && s.on) stop();
    const focused = camFocus.slot === slot;
    if (s.on && focused && !s.focused) s.eng.restart();
    s.focused = focused;
    if (s.on && s.eng.drawn() && !s.eng.still()) {
      if (mat.map !== s.tex) {
        mat.map = s.tex;
        mat.needsUpdate = true;
      }
      s.tex.needsUpdate = true;
    } else if (mat.map !== texture) {
      mat.map = texture;
      mat.needsUpdate = true;
    }
    let shade = 1;
    if (zone === "thoughts" && !hero) {
      const t = THREE.MathUtils.smoothstep(d, 7, 2.6) * (facing > 0 ? 1 : 0.4);
      s.lit += (0.16 + 0.98 * t - s.lit) * Math.min(1, dt * 2.2);
      shade = Math.min(1, s.lit);
      U.uNear.value *= 0.3 + 0.7 * t;
    }
    if (!hero) {
      const mine = region === zone;
      const on = mine && (lights.all ? 1 : camFocus.slot ? (camFocus.slot === slot ? 1 : 0) : near > 0.55 ? 1 : 0);
      level.current += (on - level.current) * Math.min(1, dt * (on ? 2.2 : 1.6));
      const k = level.current;
      U.uNear.value *= 1 - 0.85 * k;
      mat.color.copy(tint).lerp(pure, k).multiplyScalar(Math.max(shade, k) * (0.88 + 0.12 * k));
    } else if (zone === "thoughts") mat.color.setScalar(s.lit);
    window.__afaeLive[key] = s.on && mat.map === s.tex;
  });
  const ok = (e) => regionAt(pose.x, pose.z) === home && (e.stopPropagation(), true);
  return (
    <group position={[slot.x, 1.62, slot.z]} rotation={[0, slot.rotY, 0]}>
      <mesh position={[0, 0, -0.03]} material={glow}>
        <planeGeometry args={[w + 1.02, h + 1.02]} />
      </mesh>
      <mesh position={[0, 0, -0.02]} material={moulding}>
        <planeGeometry args={[w + 0.2, h + 0.2]} />
      </mesh>
      <mesh position={[0, 0, -0.016]} material={lip}>
        <planeGeometry args={[w + 0.15, h + 0.15]} />
      </mesh>
      <mesh position={[0, 0, -0.012]} material={frame}>
        <planeGeometry args={[w + 0.12, h + 0.12]} />
      </mesh>
      {!hero && <GalleryLight w={w} h={h} level={level} />}
      {hero ? (
        <mesh material={mat} userData={{ hero: zone }} {...archTap(zone, onEnter)}>
          <planeGeometry args={[w, h]} />
        </mesh>
      ) : (
      <mesh
        ref={(m) => {
          if (m) meshesRef.current[key] = m;
          else delete meshesRef.current[key];
        }}
        material={mat}
        userData={{ paintingId: painting.id, zone: home }}
        onPointerOver={(e) => ok(e) && onHover(painting.id)}
        onPointerOut={() => onHover(null)}
        onClick={(e) => {
          if (!ok(e)) return noteTap(`mesh-region:${regionAt(pose.x, pose.z)}/${home}`);
          if (e.delta > 16) return noteTap(`mesh-drag:${Math.round(e.delta)}`);
          onOpen(painting);
        }}
        onPointerDown={() => live.current.eng?.hold(true)}
        onPointerUp={() => live.current.eng?.hold(false)}
        onPointerLeave={() => live.current.eng?.hold(false)}
      >
        <planeGeometry args={[w, h]} />
      </mesh>
      )}
    </group>
  );
}

function Flat({ pos, rot, size, mat }) {
  return (
    <mesh position={pos} rotation={rot} material={mat}>
      <planeGeometry args={size} />
    </mesh>
  );
}

function LoveShell({ m }) {
  const H = ROOM_H.love;
  return (
    <group position={[LOVE_C.x, 0, LOVE_C.z]}>
      <mesh position={[0, H / 2, 0]} material={m.curved}>
        <cylinderGeometry args={[LOVE_C.r, LOVE_C.r, H, 72, 1, true, Math.PI / 2 + LOVE_GAP, 2 * Math.PI - 2 * LOVE_GAP]} />
      </mesh>
      <mesh position={[0, (ARCH.h + H) / 2, 0]} material={m.curved}>
        <cylinderGeometry args={[LOVE_C.r, LOVE_C.r, H - ARCH.h, 8, 1, true, Math.PI / 2 - LOVE_GAP, 2 * LOVE_GAP]} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} material={m.floor}>
        <circleGeometry args={[LOVE_C.r, 64]} />
      </mesh>
      <mesh position={[0, H, 0]} rotation={[Math.PI / 2, 0, 0]} material={m.ceil}>
        <circleGeometry args={[LOVE_C.r, 64]} />
      </mesh>
    </group>
  );
}

function BoxShell({ m, x0, x1, z0, z1, H, front }) {
  const cx = (x0 + x1) / 2;
  const cz = (z0 + z1) / 2;
  const walls = [
    { a: [x0, z0], b: [x1, z0], n: [0, 1] },
    { a: [x1, z0], b: [x1, z1], n: [-1, 0] },
    { a: [x0, z1], b: [x1, z1], n: [0, -1] },
    { a: [x0, z0], b: [x0, z1], n: [1, 0] },
  ];
  return (
    <>
      {walls.map((w, i) => (
        <Wall key={i} {...w} H={H} mat={m.wall} holes={i === front.wall ? [{ c: front.c, w: ARCH.w, h: ARCH.h }] : []} />
      ))}
      <Flat pos={[cx, 0, cz]} rot={[-Math.PI / 2, 0, 0]} size={[x1 - x0, z1 - z0]} mat={m.floor} />
      <Flat pos={[cx, H, cz]} rot={[Math.PI / 2, 0, 0]} size={[x1 - x0, z1 - z0]} mat={m.ceil} />
    </>
  );
}

function CorridorShell({ m, u }) {
  const H = ROOM_H.thoughts;
  const shafts = useMemo(() => [12, 17, 22, 27, 32].map((z, i) => ({ z, mat: additive(shaftFrag, { uTime: u.uTime }), tilt: i % 2 ? 0.22 : -0.18 })), [u]);
  const x0 = corrX(CORR.z0);
  const x1 = corrX(CORR.z1);
  return (
    <>
      <mesh geometry={RIB.l.g} material={m.ribL} />
      <mesh geometry={RIB.r.g} material={m.ribR} />
      <mesh geometry={RIB.floor.g} material={m.floor} />
      <mesh geometry={RIB.ceil.g} material={m.ceil} />
      <Wall a={[x0 - CORR.hw, CORR.z0]} b={[x0 + CORR.hw, CORR.z0]} n={[0, 1]} H={H} mat={m.wall} holes={[{ c: CORR.hw + (CORR.x - x0), w: ARCH.w, h: ARCH.h }]} />
      <Flat pos={[x1, H / 2, CORR.z1]} rot={[0, Math.PI, 0]} size={[CORR.hw * 2, H]} mat={m.wall} />
      {shafts.map((s) => (
        <mesh key={s.z} position={[corrX(s.z), H / 2, s.z]} rotation={[0, 0, s.tilt]} material={s.mat}>
          <planeGeometry args={[1.5, H]} />
        </mesh>
      ))}
    </>
  );
}

const flowerGeo = new THREE.IcosahedronGeometry(0.075, 0);
function Garland() {
  const ref = useRef(null);
  const N = 70;
  useLayoutEffect(() => {
    const o = new THREE.Object3D();
    const c = new THREE.Color();
    for (let i = 0; i < N; i++) {
      const t = i / (N - 1);
      const x = -5.6 + t * 11.2;
      o.position.set(x, 4.5 - Math.sin(t * Math.PI * 3) ** 2 * 0.55, SPIRIT.z0 + 0.1);
      o.scale.set(1, 1, 0.55);
      o.rotation.set(0, 0, t * 9);
      o.updateMatrix();
      ref.current.setMatrixAt(i, o.matrix);
      ref.current.setColorAt(i, c.set(i % 3 ? "#FF9F1C" : "#FFD23F"));
    }
    ref.current.instanceMatrix.needsUpdate = true;
  }, []);
  return (
    <instancedMesh ref={ref} args={[flowerGeo, null, N]}>
      <meshBasicMaterial toneMapped={false} />
    </instancedMesh>
  );
}

function Temple({ slots, u }) {
  const mats = useMemo(
    () => ({
      niche: new THREE.MeshBasicMaterial({ color: "#4A3A80", toneMapped: false }),
      halo: new THREE.MeshBasicMaterial({ map: haloTex(), transparent: true, depthWrite: false, toneMapped: false, blending: THREE.AdditiveBlending }),
      gold: new THREE.MeshBasicMaterial({ color: "#E9B44C", toneMapped: false }),
      clay: new THREE.MeshBasicMaterial({ color: "#B5562B", toneMapped: false }),
      flames: slots.map((_, i) => additive(flameFrag, { uTime: u.uTime, uSeed: { value: i * 1.7 } })),
      wisps: [0, 1, 2].map((i) => additive(wispFrag, { uTime: u.uTime, uSeed: { value: i * 2.3 } })),
    }),
    [slots, u],
  );
  const wispRefs = useRef([]);
  useFrame(() => wispRefs.current.forEach((m) => m && (m.visible = !camFocus.slot)));
  const arch = useMemo(() => {
    const s = new THREE.Shape();
    s.moveTo(-1.1, 0);
    s.lineTo(1.1, 0);
    s.lineTo(1.1, 1.75);
    s.absarc(0, 1.75, 1.1, 0, Math.PI, false);
    s.lineTo(-1.1, 0);
    return s;
  }, []);
  return (
    <>
      {slots.map((s, i) => (
        <group key={i} position={[s.x, 0, s.z]} rotation={[0, s.rotY, 0]}>
          <mesh position={[0, 0.55, -0.025]} material={mats.niche}>
            <shapeGeometry args={[arch, 24]} />
          </mesh>
          <mesh position={[0, 2.3, -0.02]} material={mats.gold}>
            <torusGeometry args={[1.1, 0.045, 6, 36, Math.PI]} />
          </mesh>
          <group position={[1.45, 0, 0]} userData={{ diya: i }}>
            <mesh position={[0, 0.7, 0.03]} material={mats.halo}>
              <planeGeometry args={[2.2, 2.0]} />
            </mesh>
            <mesh position={[0, 0.03, 0.2]} material={mats.gold}>
              <boxGeometry args={[0.5, 0.05, 0.3]} />
            </mesh>
            <mesh position={[0, 0.09, 0.22]} material={mats.clay}>
              <cylinderGeometry args={[0.1, 0.05, 0.07, 14]} />
            </mesh>
            <mesh position={[0, 0.25, 0.23]} material={mats.flames[i]}>
              <planeGeometry args={[0.22, 0.42]} />
            </mesh>
          </group>
        </group>
      ))}
      <Garland />
      {[-2.6, 0, 2.6].map((x, i) => (
        <mesh key={x} ref={(m) => (wispRefs.current[i] = m)} position={[x, 3.0, SPIRIT.z0 + 0.6]} material={mats.wisps[i]}>
          <planeGeometry args={[0.8, 2.8]} />
        </mesh>
      ))}
    </>
  );
}

export function Rooms({ u, groups, slots, texById, fallback, region, meshesRef, onHover, onOpen, onEnter }) {
  const mats = useMemo(
    () =>
      Object.fromEntries(
        SECTION_IDS.map((id) => {
          const a = texById[groups[id][0]?.id] || fallback;
          const b = texById[groups[id][1]?.id] || a;
          const m = { wall: roomMaterial(id, a, b, 0, u), floor: roomMaterial(id, a, b, 1, u, { side: THREE.DoubleSide }), ceil: roomMaterial(id, a, b, 2, u, { side: THREE.DoubleSide }) };
          if (id === "love") m.curved = roomMaterial(id, a, b, 0, u, { uvLen: LOVE_C.r * (2 * Math.PI - 2 * LOVE_GAP), side: THREE.BackSide });
          if (id === "thoughts") {
            m.ribL = roomMaterial(id, a, b, 0, u, { uvLen: RIB.l.len, side: THREE.DoubleSide });
            m.ribR = roomMaterial(id, a, b, 0, u, { uvLen: RIB.r.len, side: THREE.DoubleSide });
          }
          return [id, m];
        }),
      ),
    [u, groups, texById, fallback],
  );
  const common = { region, meshesRef, onHover, onOpen, onEnter };
  // rooms are drawn in the hall only for a short warm-up (textures uploaded), then hidden behind the walls until entered
  const [warm, setWarm] = useState(true);
  useEffect(() => {
    const id = setTimeout(() => setWarm(false), 4000);
    return () => clearTimeout(id);
  }, []);
  const shells = {
    love: <LoveShell m={mats.love} />,
    nature: <BoxShell m={mats.nature} x0={NATURE.x0} x1={NATURE.x1} z0={-NATURE.hz} z1={NATURE.hz} H={ROOM_H.nature} front={{ wall: 3, c: NATURE.hz }} />,
    spirit: <BoxShell m={mats.spirit} x0={-SPIRIT.hx} x1={SPIRIT.hx} z0={SPIRIT.z0} z1={SPIRIT.z1} H={ROOM_H.spirit} front={{ wall: 2, c: SPIRIT.hx }} />,
    thoughts: <CorridorShell m={mats.thoughts} u={u} />,
    fire: <BoxShell m={mats.fire} x0={FIRE.x0} x1={FIRE.x1} z0={FIRE.z0} z1={FIRE.z1} H={ROOM_H.fire} front={{ wall: 0, c: -7 - FIRE.x0 }} />,
  };
  return (
    <>
      <FeelingWall u={u} onEnter={onEnter} onMemory={() => glideTo("memory")} peeks={SECTION_IDS.reduce((o, id) => ({ ...o, [id]: texById[groups[id]?.[0]?.id] }), {})} />
      {SECTION_IDS.map((id) => (
        <group key={id} visible={region === id || (region === "hall" && warm)} userData={{ room: id }}>
          {shells[id]}
          <ReturnPortal id={id} onBack={() => glideTo("hall")} />
          {groups[id].map((p) => slots[p.id] && <HungPainting key={p.id} painting={p} slot={slots[p.id]} texture={texById[p.id] || fallback} zone={id} {...common} />)}
          {id === "spirit" && <Temple slots={groups[id].map((p) => slots[p.id]).filter(Boolean)} u={u} />}
        </group>
      ))}
    </>
  );
}
